import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

class TanProductCustomizer extends HTMLElement {
  /** @type {number} */
  #basePrice = 0;

  /** @type {Array<{id: string, price: number, title: string}> | null} */
  #mainVariants = null;

  /** @type {Array<{id: string, price: number}>} */
  #feeVariants = [];

  /** @type {Map<string, Promise<void>>} */
  #feeVariantPromises = new Map();

  /** @type {Set<string>} */
  #loadedFeeUrls = new Set();

  /** @type {Set<string>} */
  #failedFeeUrls = new Set();

  /** @type {AbortController} */
  #abortController = new AbortController();

  #translations = {};

  /** @type {Array<{src: string, srcset?: string, sources?: Array<[number, string]>, width?: number, height?: number, alt?: string}>} */
  #exampleImages = [];

  /** @type {string} */
  #exampleTitle = '';

  /** @type {number} */
  #exampleIndex = 0;

  /** @type {HTMLButtonElement | null} */
  #exampleOpener = null;

  /** @type {number} */
  #exampleRenderToken = 0;

  /** @type {number | null} */
  #examplePointerStart = null;

  /** @type {string} */
  #previousMountingChoiceId = 'wall_mount';

  /** @type {string} */
  #previousRemoteChoiceId = 'yes';

  /** @type {boolean} */
  #rgbControllerActive = false;

  /** @type {'standard' | 'rgb'} */
  #controllerLockMode = 'standard';

  connectedCallback() {
    this.#translations = this.#readTranslations();
    this.#exampleTitle = this.#translate('general.example');
    this.#basePrice = Number(this.dataset.basePrice) || 0;
    this.#syncBasePrice();

    const { signal } = this.#abortController;
    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('input', this.#handleInput, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    document.addEventListener('shopify:section:load', this.#syncExampleTriggers, { signal });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.#syncExampleTriggers, { signal, once: true });
    }

    const exampleDialog = this.querySelector('[data-example-dialog]');
    exampleDialog?.addEventListener('cancel', this.#handleExampleCancel, { signal });

    const exampleStage = this.querySelector('[data-example-stage]');
    exampleStage?.addEventListener('pointerdown', this.#handleExamplePointerDown, { signal });
    exampleStage?.addEventListener('pointerup', this.#handleExamplePointerUp, { signal });
    exampleStage?.addEventListener('pointercancel', this.#handleExamplePointerCancel, { signal });

    const eventTarget = this.closest('.shopify-section, dialog');
    eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    eventTarget?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#syncConditionalFields();
    this.#updatePrice();

    // Fee data is intentionally loaded only after a paid option is selected.
    // Normal product-page visits therefore make no customization-fee requests.
  }

  disconnectedCallback() {
    const exampleDialog = this.querySelector('[data-example-dialog]');
    if (exampleDialog instanceof HTMLDialogElement && exampleDialog.open) exampleDialog.close();
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.#feeVariants = [];
    this.#feeVariantPromises.clear();
    this.#loadedFeeUrls.clear();
    this.#failedFeeUrls.clear();
    this.#translations = {};
    this.#exampleImages = [];
    this.#exampleTitle = '';
    this.#exampleIndex = 0;
    this.#exampleOpener = null;
    this.#examplePointerStart = null;
    this.#previousMountingChoiceId = 'wall_mount';
    this.#previousRemoteChoiceId = 'yes';
    this.#rgbControllerActive = false;
    this.#controllerLockMode = 'standard';
  }

  #readTranslations() {
    const source = this.querySelector('[data-tan-pc-i18n]');
    if (!(source instanceof HTMLScriptElement)) return {};

    try {
      const translations = JSON.parse(source.textContent || '{}');
      if (!translations || typeof translations !== 'object' || Array.isArray(translations)) return {};
      const decoder = document.createElement('textarea');
      return Object.fromEntries(
        Object.entries(translations)
          .filter(([, value]) => typeof value === 'string')
          .map(([key, value]) => {
            decoder.innerHTML = value;
            return [key, decoder.value];
          })
      );
    } catch {
      return {};
    }
  }

  #translate(key, replacements = {}) {
    let output = String(this.#translations[key] || '');
    Object.entries(replacements).forEach(([name, value]) => {
      output = output.split(`[[${name}]]`).join(String(value));
    });
    return output;
  }

  #handleChange = () => {
    this.#syncConditionalFields();
    this.#updatePrice();
    this.#preloadSelectedFeeBucket();
  };

  /** @param {Event} event */
  #handleInput = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      const hasValue = Boolean(String(target.value || '').trim());
      if (target.dataset.summaryHasValue === String(hasValue)) return;
      target.dataset.summaryHasValue = String(hasValue);
    }
    this.#renderConfigurationSummary();
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-example-trigger]');
    if (trigger instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#openExample(trigger);
      return;
    }

    if (target.closest('[data-example-close]')) {
      event.preventDefault();
      this.#closeExample();
      return;
    }

    if (target.closest('[data-example-previous]')) {
      event.preventDefault();
      this.#stepExample(-1);
      return;
    }

    if (target.closest('[data-example-next]')) {
      event.preventDefault();
      this.#stepExample(1);
      return;
    }

    const dialog = this.querySelector('[data-example-dialog]');
    if (dialog instanceof HTMLDialogElement && event.target === dialog) this.#closeExample();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const dialog = this.querySelector('[data-example-dialog]');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#stepExample(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#stepExample(1);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeExample();
    }
  };

  /** @param {Event} event */
  #handleExampleCancel = (event) => {
    event.preventDefault();
    this.#closeExample();
  };

  /** @param {PointerEvent} event */
  #handleExamplePointerDown = (event) => {
    if (!event.isPrimary) return;
    this.#examplePointerStart = event.clientX;
    if (event.currentTarget instanceof Element) event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  /** @param {PointerEvent} event */
  #handleExamplePointerUp = (event) => {
    if (this.#examplePointerStart === null) return;
    const delta = event.clientX - this.#examplePointerStart;
    this.#examplePointerStart = null;
    if (Math.abs(delta) < 46) return;
    this.#stepExample(delta > 0 ? -1 : 1);
  };

  #handleExamplePointerCancel = () => {
    this.#examplePointerStart = null;
  };

  #readExampleLibrary() {
    const library = new Map();
    const sources = [
      ...document.querySelectorAll('script[data-tan-nd-example-library]'),
      ...this.querySelectorAll('script[data-tan-pc-example-library]'),
    ];

    sources.forEach((source) => {
      if (!(source instanceof HTMLScriptElement)) return;
      try {
        const payload = JSON.parse(source.textContent || '');
        const groups = Array.isArray(payload?.groups) ? payload.groups : [];
        groups.forEach((group) => {
          const key = String(group?.key || '').trim();
          if (!key) return;
          const images = Array.isArray(group?.images)
            ? group.images.filter(
                (image) => image && typeof image.src === 'string' && image.src.trim()
              )
            : [];
          const current = library.get(key);
          library.set(key, {
            key,
            title: String(group?.title || current?.title || this.#translate('general.example')),
            images: [...(current?.images || []), ...images],
          });
        });
      } catch {
        // Invalid theme-editor JSON must never block product customization.
      }
    });

    return library;
  }

  #syncExampleTriggers = () => {
    const library = this.#readExampleLibrary();
    const standSelected = this.#readChoiceId('properties[Backboard style]') === 'acrylic_stand';
    this.querySelectorAll('[data-example-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLButtonElement)) return;
      const key = this.#resolveExampleKey(trigger);
      const group = library.get(key);
      const mountingUnavailable = trigger.dataset.exampleSource === 'mounting' && standSelected;
      trigger.hidden = mountingUnavailable || !group?.images.length;
      trigger.dataset.exampleResolvedKey = key;
      if (group?.title) {
        trigger.setAttribute('aria-label', this.#translate('accessibility.example', { title: group.title }));
      }
    });
  };

  /** @param {HTMLButtonElement} trigger */
  #resolveExampleKey(trigger) {
    const directKey = String(trigger.dataset.exampleKey || '').trim();
    if (directKey) return directKey;

    const source = String(trigger.dataset.exampleSource || '');
    if (source === 'color') {
      const mode = this.querySelector('input[name="properties[Color option]"]:checked');
      if (!(mode instanceof HTMLInputElement)) return '';
      if (mode.dataset.choiceId === 'solid') {
        const swatch = this.querySelector(
          'input[name="properties[Selected neon color]"]:checked'
        );
        const swatchKey =
          swatch instanceof HTMLInputElement ? String(swatch.dataset.exampleKey || '') : '';
        return swatchKey || String(mode.dataset.exampleKey || '');
      }
      return String(mode.dataset.exampleKey || '');
    }

    if (source === 'backboard-color') {
      const input = this.querySelector('input[name="properties[Backboard color]"]:checked');
      if (input instanceof HTMLInputElement) return String(input.dataset.exampleKey || '');

      // Keep compatibility with older section markup during theme-editor
      // partial refreshes while the new card group replaces the select.
      const select = this.querySelector('[data-backboard-color] select, select[data-backboard-color]');
      return select instanceof HTMLSelectElement
        ? String(select.selectedOptions[0]?.dataset.exampleKey || '')
        : '';
    }

    const controlNames = {
      usage: 'properties[Usage]',
      backboard: 'properties[Backboard style]',
      mounting: 'properties[Mounting type]',
      remote: 'properties[Remote control]',
      mockup: 'properties[Mock-up confirmation]',
      greeting: 'properties[Greeting card]',
    };
    const controlName = controlNames[source];
    if (!controlName) return '';
    const input = this.querySelector(`input[name="${controlName}"]:checked`);
    return input instanceof HTMLInputElement ? String(input.dataset.exampleKey || '') : '';
  }

  /** @param {HTMLButtonElement} trigger */
  #openExample(trigger) {
    const library = this.#readExampleLibrary();
    const key =
      this.#resolveExampleKey(trigger) ||
      String(trigger.dataset.exampleResolvedKey || trigger.dataset.exampleSource || 'selection');
    const group = library.get(key);
    this.#syncExampleTriggers();
    if (!group?.images.length || trigger.hidden) return;
    const galleryTitle = String(
      group?.title || trigger.dataset.exampleTitle || this.#translate('general.example')
    );

    const dialog = this.querySelector('[data-example-dialog]');
    const title = this.querySelector('[data-example-title]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.#exampleImages = group?.images || [];
    this.#exampleTitle = galleryTitle;
    this.#exampleIndex = 0;
    this.#exampleOpener = trigger;
    trigger.dataset.exampleResolvedKey = key;
    trigger.setAttribute(
      'aria-label',
      this.#translate('accessibility.example', { title: galleryTitle })
    );
    if (title instanceof HTMLElement) title.textContent = galleryTitle;
    this.#renderExample();
    if (!dialog.open) dialog.showModal();
  }

  #renderExample() {
    const renderToken = ++this.#exampleRenderToken;
    const dialog = this.querySelector('[data-example-dialog]');
    const stage = this.querySelector('[data-example-stage]');
    const counter = this.querySelector('[data-example-counter]');
    const previous = this.querySelector('[data-example-previous]');
    const next = this.querySelector('[data-example-next]');
    const footer = counter?.closest('footer');
    if (!(stage instanceof HTMLElement)) return;

    if (this.#exampleImages.length === 0) {
      dialog?.classList.add('is-empty');
      stage.replaceChildren();
      stage.classList.remove('is-loading');
      stage.classList.add('is-empty');
      const message = document.createElement('p');
      message.className = 'tan-pc__example-empty';
      message.textContent =
        this.dataset.exampleEmpty || this.#translate('examples.coming_soon');
      stage.append(message);
      if (counter instanceof HTMLElement) counter.textContent = '';
      if (footer instanceof HTMLElement) footer.hidden = true;
      if (previous instanceof HTMLButtonElement) previous.disabled = true;
      if (next instanceof HTMLButtonElement) next.disabled = true;
      return;
    }

    dialog?.classList.remove('is-empty');
    this.#exampleIndex =
      (this.#exampleIndex + this.#exampleImages.length) % this.#exampleImages.length;
    const imageData = this.#exampleImages[this.#exampleIndex];

    stage.replaceChildren();
    stage.classList.remove('is-empty');
    stage.classList.add('is-loading');
    if (footer instanceof HTMLElement) footer.hidden = false;
    if (counter instanceof HTMLElement) {
      counter.textContent = `${this.#exampleIndex + 1} / ${this.#exampleImages.length}`;
    }
    if (previous instanceof HTMLButtonElement) previous.disabled = this.#exampleImages.length < 2;
    if (next instanceof HTMLButtonElement) next.disabled = this.#exampleImages.length < 2;

    const image = document.createElement('img');
    image.alt = String(
      imageData.alt || this.#translate('examples.image_alt', { title: this.#exampleTitle })
    );
    image.decoding = 'async';
    image.draggable = false;
    image.loading = 'eager';
    if (Number(imageData.width) > 0) image.width = Number(imageData.width);
    if (Number(imageData.height) > 0) image.height = Number(imageData.height);

    const srcset =
      typeof imageData.srcset === 'string'
        ? imageData.srcset
        : Array.isArray(imageData.sources)
          ? imageData.sources
              .map((source) =>
                Array.isArray(source) && source.length >= 2 ? `${source[1]} ${source[0]}w` : ''
              )
              .filter(Boolean)
              .join(', ')
          : '';
    if (srcset) image.srcset = srcset;
    image.sizes =
      '(orientation: landscape) and (max-height: 600px) min(620px, calc(100vw - 48px)), ' +
      '(max-width: 599px) calc(100vw - 16px), min(920px, calc(100vw - 32px))';

    image.addEventListener(
      'load',
      () => {
        if (renderToken === this.#exampleRenderToken && stage.contains(image)) {
          stage.classList.remove('is-loading');
        }
      },
      { once: true, signal: this.#abortController.signal }
    );
    image.addEventListener(
      'error',
      () => {
        if (renderToken !== this.#exampleRenderToken || !stage.contains(image)) return;
        stage.classList.remove('is-loading');
        const message = document.createElement('p');
        message.className = 'tan-pc__example-error';
        message.textContent =
          this.dataset.exampleLoadError || this.#translate('examples.load_error');
        stage.replaceChildren(message);
      },
      { once: true, signal: this.#abortController.signal }
    );

    image.src = imageData.src;
    stage.append(image);
  }

  /** @param {number} direction */
  #stepExample(direction) {
    if (this.#exampleImages.length < 2) return;
    this.#exampleIndex =
      (this.#exampleIndex + direction + this.#exampleImages.length) % this.#exampleImages.length;
    this.#renderExample();
  }

  #closeExample() {
    this.#exampleRenderToken += 1;
    const dialog = this.querySelector('[data-example-dialog]');
    const stage = this.querySelector('[data-example-stage]');
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
    dialog?.classList.remove('is-empty');
    if (stage instanceof HTMLElement) {
      stage.classList.remove('is-empty', 'is-loading');
      stage.replaceChildren();
    }
    if (this.#exampleOpener?.isConnected) this.#exampleOpener.focus({ preventScroll: true });
    this.#exampleImages = [];
    this.#exampleTitle = this.#translate('general.example');
    this.#exampleIndex = 0;
    this.#exampleOpener = null;
    this.#examplePointerStart = null;
  }

  /** @param {Event} event */
  #handleQuantityUpdate = (event) => {
    const source = event.target;
    if (!(source instanceof Element)) return;

    const sourceProductForm = source.closest('product-form-component');
    const expectedForm = document.getElementById(this.dataset.productFormId || '');
    const expectedProductForm = expectedForm?.closest('product-form-component');
    if (expectedProductForm && sourceProductForm !== expectedProductForm) return;

    // Quantity changes do not alter the selected options or unit-price
    // properties. Update only the three visible totals instead of rebuilding
    // the complete adjustment list on every + / - click.
    const priceState = this.#getPriceState();
    const quantity = this.#getProductQuantity();
    this.#renderPriceOutputs(priceState.surcharge, quantity);
    this.#dispatchPriceChange(priceState, quantity);
  };

  #preloadSelectedFeeBucket() {
    const { pricedSelections, rawSurcharge } = this.#getPriceState();
    if (pricedSelections.length === 0 || rawSurcharge <= 0) return;

    void this.#loadFeeVariants(rawSurcharge, true).then(() => this.#updatePrice());
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!event.promise) return;

    event.promise
      .then(({ variant, detail }) => {
        const variantId = variant?.id ?? detail?.resource?.id;
        if (variantId != null) this.dataset.selectedVariantId = String(variantId);

        const currency = variant?.price?.currencyCode || this.dataset.currency || 'EUR';
        const nextBasePrice = variant?.price?.amount
          ? convertMoneyToMinorUnits(String(variant.price.amount), currency)
          : null;

        if (nextBasePrice != null && nextBasePrice > 0) {
          this.#setBasePrice(nextBasePrice);
          this.dataset.currency = currency;
        } else {
          const serverProductForm = detail?.html?.querySelector?.('product-form-component');
          const serverVariantId = serverProductForm?.dataset?.currentVariantId;
          const serverVariantPrice = Number(serverProductForm?.dataset?.currentVariantPrice);

          if (String(serverVariantId || '') === String(variantId || '') && serverVariantPrice > 0) {
            this.#setBasePrice(serverVariantPrice);
          } else {
            this.#syncBasePrice();
          }
        }

        this.#syncSelectedColorName();
        this.#updatePrice();
        this.#preloadSelectedFeeBucket();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[tan-product-customizer] Variant update failed:', error);
      });
  };

  #syncConditionalFields() {
    const colorMode = this.#readChoiceId('properties[Color option]');
    this.#togglePanel('other-color', colorMode === 'solid');
    this.#togglePanel('rgb', colorMode === 'rgb');
    this.#togglePanel('color-combination', colorMode === 'combination');
    this.#syncSelectedColorName();
    const usage = this.#readChoiceId('properties[Usage]');
    this.#syncOutdoorAdapterOptions(usage === 'outdoor');
    this.#syncControllerOptions(colorMode === 'rgb', usage === 'outdoor');
    this.#syncMountingOptions();

    const rushOrder = this.#readChoiceId('properties[Rush order]');
    this.#togglePanel('rush-notes', rushOrder === 'express');

    const greetingCard = this.#readChoiceId('properties[Greeting card]');
    this.#togglePanel('greeting-message', greetingCard === 'yes');

    const mockupConfirmation = this.#readChoiceId('properties[Mock-up confirmation]');
    const requiresMockupApproval = mockupConfirmation === 'approval';
    const proceedHelp = this.querySelector('[data-mockup-help="proceed"]');
    const approvalHelp = this.querySelector('[data-mockup-help="approval"]');
    if (proceedHelp instanceof HTMLElement) proceedHelp.hidden = requiresMockupApproval;
    if (approvalHelp instanceof HTMLElement) approvalHelp.hidden = !requiresMockupApproval;
    this.#syncExampleTriggers();
  }

  /** @param {boolean} rgbSelected @param {boolean} outdoorSelected */
  #syncControllerOptions(rgbSelected, outdoorSelected) {
    const yesInput = this.querySelector('[data-remote-yes]');
    const noInput = this.querySelector('[data-remote-no]');
    const noChoice = this.querySelector('[data-remote-no-choice]');
    const yesChoice = this.querySelector('[data-remote-yes-choice]');
    const options = this.querySelector('[data-remote-options]');
    const heading = this.querySelector('[data-remote-heading]');
    const yesLabel = this.querySelector('[data-remote-yes-label]');
    const noLabel = this.querySelector('[data-remote-no-label]');
    const rgbHelp = this.querySelector('[data-rgb-controller-help]');
    const outdoorAdapterHelp = this.querySelector('[data-outdoor-adapter-dimmer-help]');
    const controllerProperty = this.querySelector('[data-rgb-controller-property]');

    if (!(yesInput instanceof HTMLInputElement) || !(noInput instanceof HTMLInputElement)) return;

    const nextLockMode = rgbSelected ? 'rgb' : 'standard';

    if (this.#controllerLockMode === 'standard' && nextLockMode !== 'standard') {
      const checked = this.querySelector('input[name="properties[Remote control]"]:checked');
      if (checked instanceof HTMLInputElement) {
        this.#previousRemoteChoiceId = String(checked.dataset.choiceId || 'yes');
      }
    }

    if (nextLockMode === 'rgb') {
      yesInput.checked = true;
      yesInput.disabled = true;
      noInput.disabled = true;
      if (yesChoice instanceof HTMLElement) yesChoice.hidden = false;
      if (noChoice instanceof HTMLElement) noChoice.hidden = true;
      if (yesChoice instanceof HTMLElement) yesChoice.classList.add('is-required-selection');
      if (noChoice instanceof HTMLElement) noChoice.classList.remove('is-required-selection');
      if (options instanceof HTMLElement) {
        options.classList.add('is-rgb-controller');
        options.classList.remove('is-outdoor-adapter');
      }
      if (heading instanceof HTMLElement) {
        heading.textContent = this.#translate('finish.rgb_controller');
      }
      if (yesLabel instanceof HTMLElement) yesLabel.textContent = this.#translate('general.included');
      if (noLabel instanceof HTMLElement) noLabel.textContent = this.#translate('general.no');
      if (rgbHelp instanceof HTMLElement) rgbHelp.hidden = false;
      if (outdoorAdapterHelp instanceof HTMLElement) outdoorAdapterHelp.hidden = true;
      if (controllerProperty instanceof HTMLInputElement) {
        controllerProperty.value = 'Included';
        controllerProperty.setAttribute('value', 'Included');
        controllerProperty.disabled = false;
      }
    } else {
      yesInput.disabled = false;
      noInput.disabled = false;
      if (this.#controllerLockMode !== 'standard') {
        const restoredInput =
          Array.from(this.querySelectorAll('input[name="properties[Remote control]"]')).find(
            (input) =>
              input instanceof HTMLInputElement &&
              input.dataset.choiceId === this.#previousRemoteChoiceId
          ) || yesInput;
        if (restoredInput instanceof HTMLInputElement) restoredInput.checked = true;
      } else {
        const checked = this.querySelector('input[name="properties[Remote control]"]:checked');
        if (checked instanceof HTMLInputElement) {
          this.#previousRemoteChoiceId = String(checked.dataset.choiceId || 'yes');
        }
      }
      if (yesChoice instanceof HTMLElement) yesChoice.hidden = false;
      if (noChoice instanceof HTMLElement) noChoice.hidden = false;
      if (yesChoice instanceof HTMLElement) yesChoice.classList.remove('is-required-selection');
      if (noChoice instanceof HTMLElement) noChoice.classList.remove('is-required-selection');
      if (options instanceof HTMLElement) {
        options.classList.remove('is-rgb-controller');
        options.classList.remove('is-outdoor-adapter');
      }
      if (heading instanceof HTMLElement) heading.textContent = this.#translate('finish.remote_heading');
      if (yesLabel instanceof HTMLElement) yesLabel.textContent = this.#translate('finish.remote_yes');
      if (noLabel instanceof HTMLElement) noLabel.textContent = this.#translate('general.no');
      if (rgbHelp instanceof HTMLElement) rgbHelp.hidden = true;
      if (outdoorAdapterHelp instanceof HTMLElement) {
        outdoorAdapterHelp.hidden = !(outdoorSelected && yesInput.checked);
      }
      if (controllerProperty instanceof HTMLInputElement) controllerProperty.disabled = true;
    }

    this.#rgbControllerActive = rgbSelected;
    this.#controllerLockMode = nextLockMode;
  }

  /** @param {boolean} outdoorSelected */
  #syncOutdoorAdapterOptions(outdoorSelected) {
    const standardAdapter = this.querySelector('[data-outdoor-adapter-standard]');
    const outdoorAdapter = this.querySelector('[data-outdoor-adapter-rated]');

    if (!outdoorSelected) {
      if (standardAdapter instanceof HTMLInputElement) standardAdapter.checked = true;
      if (outdoorAdapter instanceof HTMLInputElement) outdoorAdapter.checked = false;
    } else if (
      standardAdapter instanceof HTMLInputElement &&
      outdoorAdapter instanceof HTMLInputElement &&
      !standardAdapter.checked &&
      !outdoorAdapter.checked
    ) {
      standardAdapter.checked = true;
    }

    this.#togglePanel('outdoor-adapter', outdoorSelected);
  }

  #syncMountingOptions() {
    const usage = this.#readChoiceId('properties[Usage]');
    const backboard = this.#readChoiceId('properties[Backboard style]');
    const isStand = backboard === 'acrylic_stand';
    const isBox = backboard === 'box_style';
    const isOutdoor = usage === 'outdoor';

    const options = this.querySelector('[data-mounting-options]');
    const standNotice = this.querySelector('[data-mounting-stand-notice]');
    const help = this.querySelector('[data-mounting-help]');
    const example = this.querySelector('[data-mounting-example]');
    const notApplicable = this.querySelector('[data-mounting-not-applicable]');
    const strips = this.querySelector('[data-mounting-3m]');
    const mountingInputs = Array.from(
      this.querySelectorAll('input[name="properties[Mounting type]"][type="radio"]')
    ).filter((input) => input instanceof HTMLInputElement);

    const checked = mountingInputs.find((input) => input.checked);
    if (!isStand && checked && !checked.disabled) {
      this.#previousMountingChoiceId = String(checked.dataset.choiceId || 'wall_mount');
    }

    if (options instanceof HTMLElement) options.hidden = isStand;
    if (standNotice instanceof HTMLElement) standNotice.hidden = !isStand;
    if (help instanceof HTMLElement) help.hidden = isStand;
    if (example instanceof HTMLElement) example.hidden = isStand;
    if (notApplicable instanceof HTMLInputElement) notApplicable.disabled = !isStand;

    mountingInputs.forEach((input) => {
      input.disabled = isStand;
    });

    if (isStand) return;

    const stripsAllowed = !isOutdoor && !isBox;
    if (strips instanceof HTMLInputElement) strips.disabled = !stripsAllowed;

    const selectedAfterRules = mountingInputs.find((input) => input.checked && !input.disabled);
    if (!selectedAfterRules) {
      const preferred = mountingInputs.find(
        (input) => input.dataset.choiceId === this.#previousMountingChoiceId && !input.disabled
      );
      const wallMount = mountingInputs.find(
        (input) => input.dataset.choiceId === 'wall_mount' && !input.disabled
      );
      const fallback = preferred || wallMount || mountingInputs.find((input) => !input.disabled);
      if (fallback) fallback.checked = true;
    }
  }

  /** @param {string} name */
  #readCheckedControl(name) {
    const input = this.querySelector(`input[name="${name}"]:checked`);
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @param {string} name */
  #readChoiceId(name) {
    return String(this.#readCheckedControl(name)?.dataset.choiceId || '').trim();
  }

  /** @param {string} name */
  #readDisplayLabel(name) {
    const input = this.#readCheckedControl(name);
    return input
      ? String(input.dataset.displayLabel || input.value || '').trim()
      : '';
  }

  /** @param {string} name */
  #readTextValue(name) {
    const input = this.querySelector(`textarea[name="${name}"], input[name="${name}"]`);
    return input instanceof HTMLTextAreaElement || input instanceof HTMLInputElement
      ? String(input.value || '').trim()
      : '';
  }

  #getSelectedVariantLabel() {
    const selectedVariantId = String(this.dataset.selectedVariantId || '');
    const variant = this.#getMainVariants().find((item) => item.id === selectedVariantId);
    const title = String(variant?.title || '').trim();
    return !title || title === 'Default Title' ? this.#translate('general.one_size') : title;
  }

  #renderConfigurationSummary() {
    const list = this.querySelector('[data-configuration-list]');
    if (!(list instanceof HTMLDListElement)) return;

    const colorMode = this.#readChoiceId('properties[Color option]');
    let color = this.#readDisplayLabel('properties[Color option]');
    if (colorMode === 'as_shown') color = this.#translate('color.shown_summary');
    if (colorMode === 'solid') {
      const selectedColor = this.#readDisplayLabel('properties[Selected neon color]');
      color = selectedColor
        ? this.#translate('color.solid_summary', { color: selectedColor })
        : this.#translate('color.solid');
    }
    if (colorMode === 'combination') {
      const details = this.#readTextValue('properties[Color combination details]');
      color = details
        ? this.#translate('color.details_summary')
        : this.#translate('color.combination');
    }
    if (colorMode === 'rgb') color = this.#translate('color.rgb_summary');

    const usageChoice = this.#readChoiceId('properties[Usage]');
    const usage = this.#readDisplayLabel('properties[Usage]');
    const powerAdapter =
      usageChoice === 'outdoor'
        ? this.#readDisplayLabel('properties[Power adapter]')
        : '';
    const backboardChoice = this.#readChoiceId('properties[Backboard style]');
    const backboard = this.#readDisplayLabel('properties[Backboard style]');
    const backboardColor = this.#readDisplayLabel('properties[Backboard color]');
    const mounting =
      backboardChoice === 'acrylic_stand'
        ? this.#translate('summary.stand_mounting')
        : this.#readDisplayLabel('properties[Mounting type]');
    const remoteChoice = this.#readChoiceId('properties[Remote control]');
    const remote =
      colorMode === 'rgb'
        ? this.#translate('summary.rgb_controller')
        : remoteChoice === 'no'
          ? this.#translate('summary.remote_none')
          : this.#translate('summary.remote_included');
    const productionChoice = this.#readChoiceId('properties[Rush order]');
    const production =
      productionChoice === 'express'
        ? this.#translate('finish.express_production')
        : this.#translate('finish.standard_production');
    const greetingChoice = this.#readChoiceId('properties[Greeting card]');
    const greetingLabel = this.#readDisplayLabel('properties[Greeting card]');
    const greetingMessage = this.#readTextValue('properties[Greeting card message]');
    const greeting =
      greetingChoice === 'yes' && greetingMessage
        ? this.#translate('summary.greeting_added')
        : greetingLabel;
    const mockupChoice = this.#readChoiceId('properties[Mock-up confirmation]');
    const mockup =
      mockupChoice === 'approval'
        ? this.#translate('summary.mockup_approval')
        : this.#translate('summary.mockup_proceed');

    const rows = [
      [this.#translate('summary.size'), this.#getSelectedVariantLabel()],
      [this.#translate('summary.colour'), color],
      [this.#translate('summary.use'), usage],
      [this.#translate('summary.power_adapter'), powerAdapter],
      [this.#translate('summary.backboard'), backboard],
      [this.#translate('summary.backboard_colour'), backboardColor],
      [this.#translate('summary.mounting'), mounting],
      [this.#translate('summary.controller'), remote],
      [this.#translate('summary.production'), production],
      [this.#translate('summary.greeting'), greeting],
      [this.#translate('summary.mockup'), mockup],
    ].filter(([, value]) => value);

    list.replaceChildren();
    rows.forEach(([label, value]) => {
      const row = document.createElement('div');
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      term.textContent = label;
      description.textContent = value;
      row.append(term, description);
      list.append(row);
    });
  }

  #syncSelectedColorName() {
    const selected = this.querySelector(
      'input[name="properties[Selected neon color]"]:checked'
    );
    const output = this.querySelector('[data-selected-color-name]');
    if (!(output instanceof HTMLElement)) return;

    const name =
      selected instanceof HTMLInputElement
        ? String(selected.dataset.displayLabel || selected.value || '').trim()
        : '';

    output.textContent = name;
    output.hidden = !name;
  }

  /**
   * @param {string} name
   * @param {boolean} visible
   */
  #togglePanel(name, visible) {
    const panel = this.querySelector(`[data-conditional-panel="${name}"]`);
    if (!(panel instanceof HTMLElement)) return;

    panel.hidden = !visible;
    panel.querySelectorAll('input, select, textarea').forEach((control) => {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement || control instanceof HTMLTextAreaElement)) {
        return;
      }

      control.disabled = !visible;
      if (control.dataset.requiredWhenVisible === 'true') {
        control.required = visible;
      }
    });
  }

  /**
   * Returns the paid fee line that must be added together with the main product.
   * Shopify does not allow a theme to change a variant price, so every supported
   * surcharge is represented by one real, hidden fee-product variant. Using one
   * exact-price variant keeps the cart and Shopify checkout to a single fee line.
   *
   * @param {number} [productQuantity=1]
   * @returns {Promise<{required: boolean, ok: boolean, groupId?: string, surcharge?: number, percentage?: number, feeVariantId?: string, feeUnit?: string, upgradeNames?: string, pricingVersion?: string, items?: Array<{variantId: string, quantity: number, properties: Record<string, string>}>, error?: string}>}
   */
  async getCartConfiguration(productQuantity = 1) {
    let { percentage, fixedSurcharge, surcharge, configuredPrice, pricedSelections, rawSurcharge } =
      this.#getPriceState();

    if (pricedSelections.length > 0 && percentage > 0 && this.#basePrice <= 0) {
      const error = this.#translate('errors.base_price');
      this.#setFeeError(true, error);
      return { required: true, ok: false, error };
    }

    if (pricedSelections.length === 0 || rawSurcharge <= 0) {
      this.#setFeeError(false);
      return { required: false, ok: true };
    }

    const feeVariants = await this.#loadFeeVariants(rawSurcharge, true);
    if (feeVariants.length === 0) {
      console.warn(
        '[tan-product-customizer] No customization fee products are accessible in the active market. ' +
          'Make sure all 10 Product Customization products are active, published to Online Store, and included in every active market catalog.'
      );
      const error = this.#translate('errors.pricing_unavailable');
      this.#setFeeError(true, error);
      return { required: true, ok: false, error };
    }

    const feeVariant = this.#findClosestFeeVariant(rawSurcharge, feeVariants);
    if (!feeVariant) {
      const feeProductTitle = this.dataset.feeProductTitle || 'Product Customization';
      const loadedPrices = [...new Set(feeVariants.map((variant) => variant.price))].sort((a, b) => a - b);

      console.warn('[tan-product-customizer] A fee variant near the calculated amount is missing.', {
        calculatedPrice: this.#formatPrice(rawSurcharge),
        calculatedPriceMinorUnits: rawSurcharge,
        feeProductTitle,
        loadedVariantCount: feeVariants.length,
        loadedRange: loadedPrices.length
          ? `${this.#formatPrice(loadedPrices[0])} – ${this.#formatPrice(loadedPrices.at(-1))}`
          : '',
      });

      const error = this.#translate('errors.fee_out_of_range', {
        amount: this.#formatPrice(rawSurcharge),
      });
      this.#setFeeError(true, error);
      return { required: true, ok: false, error };
    }

    surcharge = feeVariant.price;
    configuredPrice = this.#basePrice + surcharge;

    const groupId = `tan-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
    const upgradeNames = pricedSelections.map((control) => control.dataset.priceLabel || control.value).join(', ');
    const feeUnit = this.#formatPrice(feeVariant.price);
    const pricingVersion = 'tan-theme-v1';
    const normalizedProductQuantity = Math.max(1, Math.round(Number(productQuantity) || 1));

    const items = [
      {
        variantId: String(feeVariant.id),
        quantity: normalizedProductQuantity,
        properties: {
          '_TAN fee': 'true',
          '_TAN configuration': groupId,
          '_TAN upgrades': upgradeNames,
          '_TAN fee unit': feeUnit,
          '_TAN customization percent': `${percentage}%`,
          ...(fixedSurcharge > 0
            ? { '_TAN fixed upgrades': this.#formatPrice(fixedSurcharge) }
            : {}),
          '_TAN pricing version': pricingVersion,
        },
      },
    ];

    this.#setFeeError(false);
    return {
      required: true,
      ok: true,
      groupId,
      surcharge,
      percentage,
      fixedSurcharge,
      feeVariantId: String(feeVariant.id),
      feeUnit,
      upgradeNames,
      pricingVersion,
      items,
      configuredPrice,
    };
  }

  #getPriceState() {
    this.#syncBasePrice();

    const pricedSelections = Array.from(
      this.querySelectorAll(
        'input[data-price-percent]:checked, option[data-price-percent]:checked, input[data-price-fixed-shop-minor]:checked, option[data-price-fixed-shop-minor]:checked'
      )
    ).filter(
      (control) =>
        (control instanceof HTMLInputElement || control instanceof HTMLOptionElement) &&
        !control.disabled &&
        ((Number(control.dataset.pricePercent) || 0) > 0 ||
          (Number(control.dataset.priceFixedShopMinor) || 0) > 0)
    );
    const percentage = pricedSelections.reduce(
      (total, control) => total + (Number(control.dataset.pricePercent) || 0),
      0
    );
    const fixedSurcharge = pricedSelections.reduce(
      (total, control) =>
        total + this.#convertShopMinorToActiveMinor(Number(control.dataset.priceFixedShopMinor) || 0),
      0
    );
    const rawSurcharge = Math.round((this.#basePrice * percentage) / 100) + fixedSurcharge;
    const matchedFeeVariant = this.#feeVariants.length
      ? this.#findClosestFeeVariant(rawSurcharge, this.#feeVariants)
      : null;
    // EUR is the shop's base currency. Until the active-market fee prices have
    // loaded, show a whole-unit estimate; it is replaced with the actual variant
    // price immediately after the background request finishes.
    const currencyMinorUnit = this.#getCurrencyMinorUnit(this.dataset.currency || 'EUR');
    const surcharge =
      matchedFeeVariant?.price ?? Math.round(rawSurcharge / currencyMinorUnit) * currencyMinorUnit;

    return {
      pricedSelections,
      percentage,
      fixedSurcharge,
      rawSurcharge,
      surcharge,
      configuredPrice: this.#basePrice + surcharge,
    };
  }

  /**
   * Selects the published whole-euro fee closest to the calculated percentage.
   * The tolerance is derived from the active market's observed price step, so
   * converted currencies remain supported without accepting out-of-range fees.
   *
   * @param {number} targetPrice
   * @param {Array<{id: string, price: number}>} variants
   */
  #findClosestFeeVariant(targetPrice, variants) {
    if (!Number.isFinite(targetPrice) || targetPrice <= 0 || variants.length === 0) return null;

    const prices = [...new Set(variants.map((variant) => variant.price).filter((price) => price > 0))].sort(
      (a, b) => a - b
    );
    if (prices.length === 0) return null;

    const gaps = prices.slice(1).map((price, index) => price - prices[index]).filter((gap) => gap > 0);
    const medianGap = gaps.length ? gaps.sort((a, b) => a - b)[Math.floor(gaps.length / 2)] : 100;
    const tolerance = Math.max(2, Math.ceil(medianGap * 0.6));

    if (targetPrice < prices[0] - tolerance || targetPrice > prices.at(-1) + tolerance) return null;

    const closest = variants.reduce((candidate, variant) => {
      if (!candidate) return variant;
      return Math.abs(variant.price - targetPrice) < Math.abs(candidate.price - targetPrice) ? variant : candidate;
    }, null);

    return closest && Math.abs(closest.price - targetPrice) <= tolerance ? closest : null;
  }

  /** @param {string} currency */
  #getCurrencyMinorUnit(currency) {
    try {
      const fractionDigits = new Intl.NumberFormat('en', {
        style: 'currency',
        currency: String(currency || 'EUR').toUpperCase(),
      }).resolvedOptions().maximumFractionDigits;
      return 10 ** fractionDigits;
    } catch {
      return 100;
    }
  }

  /** @param {number} shopMinorPrice */
  #convertShopMinorToActiveMinor(shopMinorPrice) {
    const normalizedPrice = Math.max(0, Math.round(Number(shopMinorPrice) || 0));
    if (normalizedPrice <= 0) return 0;

    const currentCurrency = String(this.dataset.currency || 'EUR').toUpperCase();
    const shopCurrency = String(this.dataset.shopCurrency || currentCurrency).toUpperCase();
    if (currentCurrency === shopCurrency) return normalizedPrice;

    const marketRate = Number(window.Shopify?.currency?.rate);
    if (!Number.isFinite(marketRate) || marketRate <= 0) return normalizedPrice;

    const shopMinorUnit = this.#getCurrencyMinorUnit(shopCurrency);
    const currentMinorUnit = this.#getCurrencyMinorUnit(currentCurrency);
    return Math.round((normalizedPrice / shopMinorUnit) * marketRate * currentMinorUnit);
  }

  /** @param {number} price */
  #setBasePrice(price) {
    const normalizedPrice = Math.round(Number(price));
    if (!Number.isFinite(normalizedPrice) || normalizedPrice <= 0) return false;

    this.#basePrice = normalizedPrice;
    this.dataset.basePrice = String(normalizedPrice);
    return true;
  }

  #syncBasePrice() {
    const formId = this.dataset.productFormId;
    let form = formId ? document.getElementById(formId) : null;
    let productForm = form?.closest('product-form-component');

    if (!(form instanceof HTMLFormElement)) {
      const scope = this.closest('.shopify-section, dialog') || document;
      const productId = String(this.dataset.productId || '');
      const productForms = Array.from(scope.querySelectorAll('product-form-component'));

      productForm =
        productForms.find((candidate) => productId && candidate.dataset.productId === productId) ||
        productForms.find((candidate) => candidate.querySelector('form[data-type="add-to-cart-form"]')) ||
        null;
      form = productForm?.querySelector('form[data-type="add-to-cart-form"]') || null;
    }

    const variantInput =
      form instanceof HTMLFormElement
        ? /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'))
        : null;
    const selectedVariantId = String(variantInput?.value || this.dataset.selectedVariantId || '');

    if (selectedVariantId) {
      this.dataset.selectedVariantId = selectedVariantId;
      const matchingVariant = this.#getMainVariants().find((variant) => variant.id === selectedVariantId);
      if (matchingVariant && this.#setBasePrice(matchingVariant.price)) return true;
    }

    if (
      variantInput?.dataset.variantPriceId === selectedVariantId &&
      this.#setBasePrice(Number(variantInput.dataset.variantPrice))
    ) {
      return true;
    }

    if (
      productForm instanceof HTMLElement &&
      (!selectedVariantId || productForm.dataset.currentVariantId === selectedVariantId) &&
      this.#setBasePrice(Number(productForm.dataset.currentVariantPrice))
    ) {
      return true;
    }

    return this.#setBasePrice(Number(this.dataset.basePrice));
  }

  #getMainVariants() {
    if (this.#mainVariants) return this.#mainVariants;

    const source = this.querySelector('[data-main-product-variants]');
    if (!(source instanceof HTMLScriptElement)) return [];

    try {
      const variants = JSON.parse(source.textContent || '[]');
      if (!Array.isArray(variants)) return [];

      this.#mainVariants = variants
        .filter((variant) => Number(variant?.id) > 0 && Number(variant?.price) > 0)
        .map((variant) => ({
          id: String(variant.id),
          price: Math.round(Number(variant.price)),
          title: String(variant.title || ''),
        }));
      return this.#mainVariants;
    } catch (error) {
      console.warn('[tan-product-customizer] Invalid main product variant data:', error);
      return [];
    }
  }

  #getEmbeddedFeeVariants() {
    const source = this.querySelector('[data-fee-variants]');
    if (!(source instanceof HTMLScriptElement)) return [];

    try {
      const variants = JSON.parse(source.textContent || '[]');
      if (!Array.isArray(variants)) return [];

      return variants
        .filter(
          (variant) =>
            variant?.available !== false &&
            Number(variant?.id) > 0 &&
            String(variant?.sku || '').startsWith('TAN-PC-')
        )
        .map((variant) => ({
          id: String(variant.id),
          title: String(variant.title || ''),
          sku: String(variant.sku || ''),
          price: this.#normalizeFeePrice(variant.price),
        }))
        .filter((variant) => variant.price > 0)
        .sort((a, b) => b.price - a.price);
    } catch (error) {
      console.warn('[tan-product-customizer] Invalid fee variant data:', error);
      return [];
    }
  }

  /**
   * Shopify's Liquid data uses minor units (3720), while some product JSON
   * responses or market integrations may expose decimal strings ("37.20").
   * Normalize both representations to integer minor units before matching.
   *
   * @param {unknown} rawPrice
   * @returns {number}
   */
  #normalizeFeePrice(rawPrice) {
    if (rawPrice && typeof rawPrice === 'object' && 'amount' in rawPrice) {
      const amount = /** @type {{amount?: unknown}} */ (rawPrice).amount;
      const currencyCode = /** @type {{currencyCode?: string}} */ (rawPrice).currencyCode;
      if (amount != null) {
        return convertMoneyToMinorUnits(
          String(amount),
          currencyCode || this.dataset.currency || 'EUR'
        );
      }
    }

    if (typeof rawPrice === 'string') {
      const normalized = rawPrice.trim().replace(/\s/g, '');
      if (!normalized) return 0;

      if (/[.,]/.test(normalized)) {
        const decimalPrice = Number(normalized.replace(',', '.'));
        return Number.isFinite(decimalPrice) ? Math.round(decimalPrice * 100) : 0;
      }
    }

    const numericPrice = Number(rawPrice);
    return Number.isFinite(numericPrice) ? Math.round(numericPrice) : 0;
  }

  #getFeeProductJsonUrls(targetPrice = 0) {
    const urls = [];
    const source = this.querySelector('[data-fee-product-json-urls]');

    if (source instanceof HTMLScriptElement) {
      try {
        const parsedUrls = JSON.parse(source.textContent || '[]');
        if (Array.isArray(parsedUrls)) urls.push(...parsedUrls);
      } catch (error) {
        console.warn('[tan-product-customizer] Invalid fee product URL data:', error);
      }
    }

    if (this.dataset.feeProductJsonUrl) urls.unshift(this.dataset.feeProductJsonUrl);

    const shopRoot = String(window.Shopify?.routes?.root || '/');
    const rootWithSlash = shopRoot.endsWith('/') ? shopRoot : `${shopRoot}/`;
    for (let start = 1; start <= 2500; start += 250) {
      const end = start + 249;
      urls.push(
        `${rootWithSlash}products/product-customization-${String(start).padStart(4, '0')}-${String(end).padStart(4, '0')}.js`
      );
    }

    const uniqueUrls = [
      ...new Set(
        urls
          .filter((url) => typeof url === 'string' && url.trim())
          .map((url) => url.trim())
      ),
    ];

    const currentCurrency = String(this.dataset.currency || 'EUR').toUpperCase();
    const shopCurrency = String(this.dataset.shopCurrency || currentCurrency).toUpperCase();
    const marketRate = Number(window.Shopify?.currency?.rate);
    const conversionRate = currentCurrency === shopCurrency ? 1 : marketRate > 0 ? marketRate : 0;

    if (targetPrice > 0 && conversionRate > 0) {
      const currentMinorUnit = this.#getCurrencyMinorUnit(currentCurrency);
      const targetShopAmount = Math.round(targetPrice / currentMinorUnit / conversionRate);
      const matchingUrl = uniqueUrls.find((url) => {
        const match = url.match(/product-customization-(\d{4})-(\d{4})\.js(?:\?|$)/);
        if (!match) return false;
        return targetShopAmount >= Number(match[1]) && targetShopAmount <= Number(match[2]);
      });
      if (matchingUrl) {
        return [matchingUrl, ...uniqueUrls.filter((url) => url !== matchingUrl)];
      }
    }

    return uniqueUrls;
  }

  /**
   * Reads the currently published fee product through Shopify's product JSON
   * endpoint. This avoids stale block-picker/Liquid data and also respects the
   * active market's variant prices. The embedded variants are kept as a safe
   * fallback if the request is unavailable.
   *
   * The estimated market-rate bucket is attempted first. When that estimate is
   * unavailable or doesn't contain a close contextual price, the remaining
   * buckets are tried one at a time and loading stops as soon as a match is
   * found. This keeps the normal path to one request while supporting fixed
   * market pricing, currencies with unusual precision, and missing FX globals.
   *
   * @param {number} [targetPrice=0]
   * @param {boolean} [findMatch=false]
   * @returns {Promise<Array<{id: string, price: number, title?: string, sku?: string}>>}
   */
  async #loadFeeVariants(targetPrice = 0, findMatch = false) {
    if (this.#feeVariants.length === 0) {
      this.#feeVariants = this.#getEmbeddedFeeVariants();
    }

    const productJsonUrls = this.#getFeeProductJsonUrls(targetPrice);
    for (const productJsonUrl of productJsonUrls) {
      if (findMatch && this.#findClosestFeeVariant(targetPrice, this.#feeVariants)) break;
      if (this.#loadedFeeUrls.has(productJsonUrl) || this.#failedFeeUrls.has(productJsonUrl)) continue;

      let request = this.#feeVariantPromises.get(productJsonUrl);
      if (!request) {
        request = (async () => {
        try {
          const response = await fetch(productJsonUrl, {
            headers: { Accept: 'application/json' },
            signal: this.#abortController.signal,
            cache: 'force-cache',
          });

          if (!response.ok) {
            this.#failedFeeUrls.add(productJsonUrl);
            return;
          }

          const product = await response.json();
          const liveVariants = Array.isArray(product?.variants)
            ? product.variants
                .filter(
                  (variant) =>
                    variant?.available !== false &&
                    Number(variant?.id) > 0 &&
                    String(variant?.sku || '').startsWith('TAN-PC-')
                )
                .map((variant) => ({
                  id: String(variant.id),
                  title: String(variant.title || ''),
                  sku: String(variant.sku || ''),
                  price: this.#normalizeFeePrice(variant.price),
                }))
                .filter((variant) => variant.price > 0)
            : [];

          const variantsById = new Map(this.#feeVariants.map((variant) => [variant.id, variant]));
          liveVariants.forEach((variant) => variantsById.set(variant.id, variant));
          this.#feeVariants = Array.from(variantsById.values()).sort((a, b) => b.price - a.price);
          this.#loadedFeeUrls.add(productJsonUrl);
        } catch (error) {
          if (error?.name === 'AbortError') return;
          this.#failedFeeUrls.add(productJsonUrl);
          console.warn(`[tan-product-customizer] Fee product data could not be loaded from ${productJsonUrl}:`, error);
        } finally {
          this.#feeVariantPromises.delete(productJsonUrl);
        }
        })();
        this.#feeVariantPromises.set(productJsonUrl, request);
      }
      await request;

      // Without a paid target only the preferred bucket should be loaded.
      if (!findMatch) break;
    }

    return this.#feeVariants;
  }

  /** @param {boolean} visible @param {string} [message] */
  #setFeeError(visible, message) {
    const errorElement = this.querySelector('[data-fee-error]');
    if (!(errorElement instanceof HTMLElement)) return;

    errorElement.hidden = !visible;
    if (visible && message) errorElement.textContent = message;
  }

  /** @param {boolean} paidUpgradeSelected */
  #syncAcceleratedCheckout(paidUpgradeSelected) {
    const productSection = this.closest('.shopify-section, dialog');
    const containers = productSection?.querySelectorAll(
      '[ref="acceleratedCheckoutButtonContainer"]:not(.tan-checkout-block), .shopify-payment-button'
    );

    containers?.forEach((container) => {
      if (!(container instanceof HTMLElement)) return;

      if (paidUpgradeSelected) {
        container.dataset.tanCustomizerHidden = 'true';
        container.hidden = true;
      } else if (container.dataset.tanCustomizerHidden === 'true') {
        delete container.dataset.tanCustomizerHidden;
        container.hidden = false;
      }
    });
  }

  #getProductQuantity() {
    const form = document.getElementById(this.dataset.productFormId || '');
    const quantityInput =
      form instanceof HTMLFormElement
        ? /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'))
        : null;

    return Math.max(1, Math.round(Number(quantityInput?.value) || 1));
  }

  /** @param {number} quantity */
  #getVolumeDiscountPercent(quantity) {
    const productSection = this.closest('.shopify-section, dialog');
    const offer = productSection?.querySelector('[data-tan-volume-offer]');
    if (!(offer instanceof HTMLElement)) return 0;

    const tiers = [
      {
        quantity: Number(offer.dataset.firstQuantity) || 0,
        discount: Number(offer.dataset.firstDiscount) || 0,
      },
      {
        quantity: Number(offer.dataset.secondQuantity) || 0,
        discount: Number(offer.dataset.secondDiscount) || 0,
      },
    ];

    return tiers.reduce((bestDiscount, tier) => {
      if (tier.quantity <= 0 || quantity < tier.quantity) return bestDiscount;
      return Math.max(bestDiscount, Math.min(100, tier.discount));
    }, 0);
  }

  /** @param {number} surcharge @param {number} quantity */
  #getDisplayedPriceState(surcharge, quantity) {
    const basePriceTotal = this.#basePrice * quantity;
    const surchargeTotal = surcharge * quantity;
    const volumeDiscountPercent = this.#getVolumeDiscountPercent(quantity);
    const volumeDiscountTotal = Math.round((basePriceTotal * volumeDiscountPercent) / 100);

    return {
      basePriceTotal,
      surchargeTotal,
      volumeDiscountPercent,
      volumeDiscountTotal,
      configuredPriceTotal: basePriceTotal + surchargeTotal - volumeDiscountTotal,
    };
  }

  /** @param {number} surcharge @param {number} quantity */
  #renderPriceOutputs(surcharge, quantity) {
    const basePriceElement = this.querySelector('[data-base-price-output]');
    const surchargeElement = this.querySelector('[data-surcharge-output]');
    const totalElement = this.querySelector('[data-configured-price-output]');
    const discountLine = this.querySelector('[data-volume-discount-line]');
    const discountElement = this.querySelector('[data-volume-discount-output]');

    const displayedPrice = this.#getDisplayedPriceState(surcharge, quantity);

    const formattedBasePrice = this.#formatPrice(displayedPrice.basePriceTotal);
    const formattedSurcharge = this.#formatPrice(displayedPrice.surchargeTotal);
    const formattedConfiguredPrice = this.#formatPrice(displayedPrice.configuredPriceTotal);

    if (basePriceElement) basePriceElement.textContent = formattedBasePrice;
    if (surchargeElement) surchargeElement.textContent = surcharge > 0 ? `+${formattedSurcharge}` : formattedSurcharge;
    if (totalElement) totalElement.textContent = formattedConfiguredPrice;

    if (discountLine instanceof HTMLElement) {
      discountLine.hidden = displayedPrice.volumeDiscountPercent <= 0;
    }
    if (discountElement) {
      const formattedDiscount = this.#formatPrice(displayedPrice.volumeDiscountTotal);
      discountElement.textContent =
        displayedPrice.volumeDiscountPercent > 0
          ? `−${formattedDiscount} (${displayedPrice.volumeDiscountPercent}%)`
          : '';
    }
  }

  /**
   * @param {{percentage: number, surcharge: number, configuredPrice: number}} priceState
   * @param {number} quantity
   */
  #dispatchPriceChange({ percentage, surcharge, configuredPrice }, quantity) {
    const displayedPrice = this.#getDisplayedPriceState(surcharge, quantity);

    this.dispatchEvent(
      new CustomEvent('tan:customizer:price-change', {
        bubbles: true,
        detail: {
          basePrice: this.#basePrice,
          percentage,
          surcharge,
          configuredPrice,
          quantity,
          basePriceTotal: displayedPrice.basePriceTotal,
          surchargeTotal: displayedPrice.surchargeTotal,
          volumeDiscountPercent: displayedPrice.volumeDiscountPercent,
          volumeDiscountTotal: displayedPrice.volumeDiscountTotal,
          configuredPriceTotal: displayedPrice.configuredPriceTotal,
          currency: this.dataset.currency,
        },
      })
    );
  }

  #updatePrice() {
    const priceState = this.#getPriceState();
    const { pricedSelections, percentage, surcharge, configuredPrice } = priceState;
    const quantity = this.#getProductQuantity();

    const breakdownElement = this.querySelector('[data-adjustment-list]');
    const percentageInput = this.querySelector('[data-percentage-property]');
    const surchargeInput = this.querySelector('[data-surcharge-property]');
    const configuredPriceInput = this.querySelector('[data-configured-price-property]');

    const formattedUnitSurcharge = this.#formatPrice(surcharge);
    const formattedConfiguredUnitPrice = this.#formatPrice(configuredPrice);

    this.#renderPriceOutputs(surcharge, quantity);

    if (percentageInput instanceof HTMLInputElement) percentageInput.value = `${percentage}%`;
    if (surchargeInput instanceof HTMLInputElement) surchargeInput.value = formattedUnitSurcharge;
    if (configuredPriceInput instanceof HTMLInputElement) configuredPriceInput.value = formattedConfiguredUnitPrice;

    this.#renderConfigurationSummary();
    this.#syncAcceleratedCheckout(pricedSelections.length > 0);
    if (pricedSelections.length === 0) {
      this.#setFeeError(false);
    } else if (this.#basePrice <= 0) {
      this.#setFeeError(true, this.#translate('errors.base_price'));
    }

    if (breakdownElement) {
      breakdownElement.replaceChildren();

      if (pricedSelections.length === 0) {
        const emptyItem = document.createElement('li');
        emptyItem.className = 'tan-pc__adjustment tan-pc__adjustment--empty';
        emptyItem.textContent = this.#translate('summary.no_upgrades');
        breakdownElement.append(emptyItem);
      } else {
        pricedSelections.forEach((control) => {
          const item = document.createElement('li');
          item.className = 'tan-pc__adjustment';

          const label = document.createElement('span');
          label.textContent =
            control.dataset.priceDisplayLabel ||
            control.dataset.displayLabel ||
            control.dataset.priceLabel ||
            control.value;

          const amount = document.createElement('strong');
          const fixedPrice = this.#convertShopMinorToActiveMinor(
            Number(control.dataset.priceFixedShopMinor) || 0
          );
          amount.textContent =
            fixedPrice > 0
              ? `+${this.#formatPrice(fixedPrice)}`
              : `+${Number(control.dataset.pricePercent) || 0}%`;

          item.append(label, amount);
          breakdownElement.append(item);
        });
      }
    }

    this.#dispatchPriceChange(priceState, quantity);
  }

  /** @param {number} value */
  #formatPrice(value) {
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'EUR';
    return formatMoney(value, moneyFormat, currency);
  }
}

if (!customElements.get('tan-product-customizer')) {
  customElements.define('tan-product-customizer', TanProductCustomizer);
}
